import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * feat-324-web-access-denied — the shared "you can't see this page" card rendered by
 * StaffRoute, AdminRoute and EvaluatorRoute once an authed user has no admitted role.
 *
 * Two variants, chosen by WHY there is no admitted role (`user.roleLookup`, recorded by
 * AuthContext from managementService.getManagementUserByEmail):
 *  - A ('failed'): the lookup itself failed (network / 5xx / backend "Internal server
 *    error"). "We couldn't load your account" + Try again (full reload re-runs
 *    initializeAuth) + Sign out. The guard's message line is NOT shown: we don't know
 *    yet whether they're allowed.
 *  - B (anything else, INCLUDING undefined): "Access denied" + the guard's unchanged
 *    message line + guidance + Sign out. Undefined must stay B — older sessions and the
 *    existing guard tests carry users without the field.
 *
 * Sign out: `await logout()` first, THEN navigate to /scorecard-login. The order matters —
 * ScorecardLoginPage redirects an authenticated user straight back to their landing page,
 * so navigating before logout would loop. Once logout() nulls `user`, the guard itself
 * unmounts this card and renders <Navigate to="/scorecard-login" replace />. Our own
 * navigate runs from an effect keyed on `signedOut`, so it only fires if this card is
 * still mounted after logout (e.g. logout did not clear the user). The `user → null`
 * update and `signedOut` land in the same batch, so there is never a double navigation.
 *
 * Design: design/NOTES.md (feature root). Copy lives in ACCESS_CARD_COPY below.
 */

export const ACCESS_CARD_COPY = {
  failedHeading: "We couldn't load your account",
  failedBody:
    'Something went wrong checking your access. This is usually temporary, so try again in a moment.',
  tryAgain: 'Try again',
  deniedHeading: 'Access denied',
  deniedGuidance: 'If you think this is wrong, contact your manager or Bespoke support.',
  signedInAs: 'Signed in as',
  signOutSecondary: 'Sign out',
  signOutPrimary: 'Sign out and switch accounts',
  signingOut: 'Signing out…',
} as const;

const LOGIN_PATH = '/scorecard-login';

const C = {
  page: 'min-h-screen bg-cream flex items-center justify-center p-6',
  card: 'w-full bg-white border border-softBorder rounded-lg shadow-sm p-8 max-w-md text-center',
  heading: 'text-2xl font-display font-semibold text-ink mb-2',
  body: 'text-sm text-inkMuted',
  guidance: 'mt-2 text-sm text-inkMuted',
  email: 'mt-4 text-sm text-inkMuted break-words',
  emailVal: 'font-semibold text-ink',
  actions: 'mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center',
  primary:
    'w-full sm:w-auto rounded-lg bg-coral px-4 py-2 text-sm font-bold text-white hover:bg-coral/90 focus:outline-none focus:ring-2 focus:ring-coral/40 disabled:opacity-50',
  secondary:
    'w-full sm:w-auto rounded-lg border border-softBorder bg-white px-4 py-2 text-sm font-semibold text-ink hover:bg-cream-dark focus:outline-none focus:ring-2 focus:ring-coral/40 disabled:opacity-50',
} as const;

interface AccessDeniedCardProps {
  /** The guard's own, unchanged message line (Variant B body). */
  message: string;
}

const AccessDeniedCard: React.FC<AccessDeniedCardProps> = ({ message }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const [signedOut, setSignedOut] = useState(false);

  useEffect(() => {
    if (signedOut) navigate(LOGIN_PATH, { replace: true });
  }, [signedOut, navigate]);

  const failed = user?.roleLookup === 'failed';
  const email = user?.email;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } finally {
      // AuthContext.logout already swallows errors and clears local state.
      setSignedOut(true);
    }
  };

  const handleTryAgain = () => {
    window.location.reload();
  };

  const signedIn = email ? (
    <p className={C.email}>
      {ACCESS_CARD_COPY.signedInAs} <span className={C.emailVal}>{email}</span>
    </p>
  ) : null;

  if (failed) {
    return (
      <div className={C.page}>
        <div role="alert" className={C.card}>
          <h1 className={C.heading}>{ACCESS_CARD_COPY.failedHeading}</h1>
          <p className={C.body}>{ACCESS_CARD_COPY.failedBody}</p>
          {signedIn}
          <div className={C.actions}>
            <button
              type="button"
              className={C.primary}
              onClick={handleTryAgain}
              disabled={signingOut}
            >
              {ACCESS_CARD_COPY.tryAgain}
            </button>
            <button
              type="button"
              className={C.secondary}
              onClick={handleSignOut}
              disabled={signingOut}
            >
              {signingOut ? ACCESS_CARD_COPY.signingOut : ACCESS_CARD_COPY.signOutSecondary}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={C.page}>
      <div className={C.card}>
        <h1 className={C.heading}>{ACCESS_CARD_COPY.deniedHeading}</h1>
        <p className={C.body}>{message}</p>
        <p className={C.guidance}>{ACCESS_CARD_COPY.deniedGuidance}</p>
        {signedIn}
        <div className={C.actions}>
          <button
            type="button"
            className={C.primary}
            onClick={handleSignOut}
            disabled={signingOut}
          >
            {signingOut ? ACCESS_CARD_COPY.signingOut : ACCESS_CARD_COPY.signOutPrimary}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessDeniedCard;
